import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Bell, Clock, Calendar, Slack, Mail, Plus, Trash2, Pause, CheckCircle } from 'lucide-react'
import { Card, CardContent } from 'components/ui/card'
import { PageHeader, StatGrid, Stat } from 'components/ui/page-header'
import { Pager, usePaged } from 'components/ui/compact'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { reminders, roadmapItems, currentUser } from '../services/database'
import { reminderEngine } from '../services/reminderEngine'
import { useToast } from '../components/ui/toast'
import { useAuth } from '../contexts/AuthContext'
import FocusPlanner from '../components/timeaudit/FocusPlanner'
import NotificationSettings from '../components/brain/NotificationSettings'
import { reminderDueAt } from '../lib/reminderTiming'
import { integrationsEnabled } from '../lib/integrations'

export default function RemindersPage() {
  const { showSuccess, showError } = useToast()
  const { user, loading: authLoading } = useAuth()
  const [allReminders, setAllReminders] = useState([])
  const [roadmapItemsMap, setRoadmapItemsMap] = useState({})
  const [loading, setLoading] = useState(true)
  const [showNewReminderForm, setShowNewReminderForm] = useState(false)
  const [filterStatus, setFilterStatus] = useState('active')
  const [loadError, setLoadError] = useState('')
  const requestNumber = useRef(0)

  const loadReminders = useCallback(async () => {
    const request = ++requestNumber.current
    if (authLoading || !user?.id) {
      if (!authLoading && !user?.id) {
        setAllReminders([])
        setRoadmapItemsMap({})
        setLoading(false)
      }
      return
    }

    try {
      const [fetchedReminders, items] = await Promise.all([reminders.list('target_date'), roadmapItems.list()])
      if (request !== requestNumber.current || (await currentUser()).id !== user.id) return
      setAllReminders(fetchedReminders)
      setRoadmapItemsMap(Object.fromEntries(items.map(item => [item.id, item])))
      setLoadError('')
    } catch (error) {
      console.error('Error loading reminders:', error)
      if (request === requestNumber.current) setLoadError('Unable to load reminders. Please refresh to retry.')
    } finally {
      if (request === requestNumber.current) setLoading(false)
    }
  }, [user?.id, authLoading])

  useEffect(() => {
    setAllReminders([]); setRoadmapItemsMap({}); setLoading(true)
    loadReminders()
    window.addEventListener('spcc:reminders-updated', loadReminders)
    // Invalidate outstanding reads on account changes and unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { requestNumber.current++; window.removeEventListener('spcc:reminders-updated', loadReminders) }
  }, [loadReminders])

  const handleSnoozeReminder = async (reminderId) => {
    try {
      await reminderEngine.snoozeReminder(reminderId, 60)
      showSuccess('Reminder snoozed for 1 hour')
      loadReminders()
    } catch (error) {
      console.error('Error snoozing reminder:', error)
      showError('Failed to snooze reminder')
    }
  }

  const handleCompleteReminder = async (reminderId) => {
    try {
      await reminderEngine.completeReminder(reminderId)
      showSuccess('Reminder marked as completed')
      loadReminders()
    } catch (error) {
      console.error('Error completing reminder:', error)
      showError('Failed to complete reminder')
    }
  }

  const handleDeleteReminder = async (reminderId) => {
    if (!window.confirm('Are you sure you want to delete this reminder?')) {
      return
    }
    try {
      await reminderEngine.deleteReminder(reminderId)
      showSuccess('Reminder deleted')
      loadReminders()
    } catch (error) {
      console.error('Error deleting reminder:', error)
      showError('Failed to delete reminder')
    }
  }

  const calculateDaysUntilDue = (targetDate) => {
    const today = new Date().setHours(0, 0, 0, 0)
    const due = new Date(`${targetDate}T00:00:00`).getTime()
    return Math.round((due - today) / (1000 * 60 * 60 * 24))
  }

  const formatDate = (dateStr) => {
    const options = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }
    return new Date(`${dateStr}T00:00:00`).toLocaleDateString(undefined, options)
  }


  const getUrgencyLabel = (daysLeft) => {
    if (daysLeft < 0) return 'Overdue'
    if (daysLeft === 0) return 'Today'
    if (daysLeft <= 3) return 'Urgent'
    if (daysLeft <= 7) return 'Soon'
    return 'Upcoming'
  }

  const filteredReminders = allReminders.filter((r) => {
    if (filterStatus === 'active') return ['active', 'snoozed'].includes(r.status) && !(Date.parse(r.snoozed_until) > Date.now())
    if (filterStatus === 'snoozed') return r.status !== 'completed' && Date.parse(r.snoozed_until) > Date.now()
    if (filterStatus === 'completed') return r.status === 'completed'
    return true
  })
  const paged = usePaged(filteredReminders, 8, filterStatus)

  return (
    <div>
      <PageHeader
        icon={Bell}
        eyebrow="Operate"
        title="Reminders"
        description="Stay ahead of upcoming discovery and delivery deadlines."
        actions={
          <Button variant="primary" onClick={() => setShowNewReminderForm(true)}>
            <Plus size={15} />
            New reminder
          </Button>
        }
      />

      <StatGrid>
        <Stat label="Active" value={allReminders.filter(r => ['active', 'snoozed'].includes(r.status) && !(Date.parse(r.snoozed_until) > Date.now())).length} />
        <Stat label="Snoozed" value={allReminders.filter(r => r.status !== 'completed' && Date.parse(r.snoozed_until) > Date.now()).length} />
        <Stat label="Completed" value={allReminders.filter(r => r.status === 'completed').length} />
      </StatGrid>
      <NotificationSettings/>
      <FocusPlanner onChange={loadReminders}/>
      {loadError && <p role="alert" className="v2-error">{loadError}</p>}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div className="segmented" role="group" aria-label="Filter reminders">
          {['active', 'snoozed', 'completed'].map((status) => (
            <button key={status} type="button" aria-pressed={filterStatus === status} onClick={() => setFilterStatus(status)}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
        <span className="tabular" style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--fg-muted)' }}>
          {filteredReminders.length} {filteredReminders.length === 1 ? 'reminder' : 'reminders'}
        </span>
        <Pager paged={paged} label="Reminder pages" />
      </div>

      <div className="grid cols-2">
        {loading ? (
          <Card style={{ gridColumn: '1 / -1' }}>
            <div className="empty"><div className="spinner" /><span>Loading reminders…</span></div>
          </Card>
        ) : filteredReminders.length === 0 ? (
          <Card style={{ gridColumn: '1 / -1' }}>
            <div className="empty">
              <Bell size={28} />
              <strong>
                {filterStatus === 'active'
                  ? 'No active reminders'
                  : filterStatus === 'snoozed'
                    ? 'No snoozed reminders'
                    : 'No completed reminders'}
              </strong>
              <span>Reminders are created automatically for initiatives, or you can add one.</span>
              <Button onClick={() => setShowNewReminderForm(true)} variant="secondary" size="sm" style={{ marginTop: 8 }}>
                <Plus size={14} />
                Create a reminder
              </Button>
            </div>
          </Card>
        ) : (
          paged.items.map((reminder) => {
            const roadmapItem = roadmapItemsMap[reminder.roadmap_item_id]
            const daysLeft = calculateDaysUntilDue(reminder.target_date)

            return (
              <Card key={reminder.id} className="interactive">
                <CardContent>
                  {/* Title and Status */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <h3 style={{ margin: '0 0 6px 0', fontSize: 14, fontWeight: 600, color: 'var(--fg)' }}>
                        {reminder.title}
                      </h3>
                      <Badge
                        className="badge-dot"
                        variant={
                          reminder.status === 'completed'
                            ? 'success'
                            : reminder.status === 'snoozed'
                              ? 'default'
                              : daysLeft <= 0 ? 'danger' : daysLeft <= 7 ? 'warning' : 'accent'
                        }
                      >
                        {reminder.status === 'completed'
                          ? 'Completed'
                          : reminder.status === 'snoozed'
                            ? 'Snoozed'
                            : getUrgencyLabel(daysLeft)}
                      </Badge>
                    </div>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {['active', 'snoozed'].includes(reminder.status) && (
                        <>
                          <Button
                            size='sm'
                            variant='ghost'
                            onClick={() => handleSnoozeReminder(reminder.id)}
                            title='Snooze for 1 hour'
                          >
                            <Pause size={16} />
                          </Button>
                          <Button
                            size='sm'
                            variant='ghost'
                            onClick={() => handleCompleteReminder(reminder.id)}
                            title='Mark as completed'
                          >
                            <CheckCircle size={16} />
                          </Button>
                        </>
                      )}
                      <Button
                        size='sm'
                        variant='ghost'
                        onClick={() => handleDeleteReminder(reminder.id)}
                        title='Delete reminder'
                      >
                        <Trash2 size={16} color='var(--danger)' />
                      </Button>
                    </div>
                  </div>

                  {/* Roadmap Item */}
                  {roadmapItem && (
                    <div
                      style={{
                        background: 'var(--bg-inset)',
                        padding: '12px',
                        borderRadius: '6px',
                        marginBottom: '12px',
                        borderLeft: '3px solid var(--accent)',
                      }}
                    >
                      <p style={{ margin: '0 0 4px 0', fontSize: '13px', fontWeight: '600', color: 'var(--fg)' }}>
                        {roadmapItem.title}
                      </p>
                      <p style={{ margin: 0, fontSize: '12px', color: 'var(--fg-muted)', lineHeight: '1.4' }}>
                        {roadmapItem.description}
                      </p>
                    </div>
                  )}

                  {/* Details */}
                  <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', fontSize: '13px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--fg-muted)' }}>
                      <Calendar size={14} />
                      <span>{formatDate(reminder.target_date)}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--fg-muted)' }}>
                      <Clock size={14} />
                      <span>{daysLeft > 0 ? `In ${daysLeft} days` : daysLeft === 0 ? 'Today' : 'Overdue'}</span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="v2-muted">Notify: {Number.isFinite(reminderDueAt(reminder, Intl.DateTimeFormat().resolvedOptions().timeZone)) ? new Date(reminderDueAt(reminder, Intl.DateTimeFormat().resolvedOptions().timeZone)).toLocaleString() : 'Choose a valid reminder date'}</p>
                  {reminder.is_sent && <p role="status">Delivered: {(reminder.delivered_channels || []).join(', ') || 'notification recorded'}</p>}
                  {reminder.delivery_error && <p className="v2-error" role="alert">{reminder.delivery_error} Delivery will retry automatically.</p>}
                  {reminder.description && (
                    <p style={{ margin: '12px 0', fontSize: '13px', color: 'var(--fg-secondary)', lineHeight: '1.5' }}>
                      {reminder.description}
                    </p>
                  )}

                  {/* Notification Channels */}
                  {reminder.notification_channels && reminder.notification_channels.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                      {reminder.notification_channels.map((channel) => (
                        <Badge key={channel} variant='outline' style={{ fontSize: '11px' }}>
                          {channel === 'in_app' && <Bell size={12} style={{ marginRight: '4px' }} />}
                          {channel === 'slack' && <Slack size={12} style={{ marginRight: '4px' }} />}
                          {channel === 'email' && <Mail size={12} style={{ marginRight: '4px' }} />}
                          {channel === 'calendar' && <Calendar size={12} style={{ marginRight: '4px' }} />}
                          {channel}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Snoozed Info */}
                  {reminder.snoozed_until && (
                    <div
                      style={{
                        marginTop: '12px',
                        padding: '8px',
                        background: 'var(--accent-soft)',
                        border: '1px solid var(--accent-border)',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: 'var(--accent-fg)',
                        fontSize: '12px',
                      }}
                    >
                      <Pause size={14} />
                      <span>Snoozed until {formatDate(reminder.snoozed_until)}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })
        )}
      </div>

      {/* Modal */}
      {showNewReminderForm && (
        <NewReminderDialog
          onClose={() => setShowNewReminderForm(false)}
          onSuccess={loadReminders}
          showSuccess={showSuccess}
          showError={showError}
        />
      )}
    </div>
  )
}

function NewReminderDialog({ onClose, onSuccess, showSuccess, showError }) {
  const [roadmapItemsList, setRoadmapItemsList] = useState([])
  const [formData, setFormData] = useState({
    roadmap_item_id: '',
    title: '',
    description: '',
    reminder_days_before: 7,
    notification_channels: ['in_app'],
    due_at: '',
  })
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    loadRoadmapItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadRoadmapItems = async () => {
    try {
      // Load all roadmap items
      const items = await roadmapItems.list('-target_date', null, {})
      // Filter out done items - only show planned and in_progress for reminders
      const activeItems = items.filter(item => item.status !== 'done')
      setRoadmapItemsList(activeItems)
    } catch (error) {
      console.error('Error loading roadmap items:', error)
      showError('Failed to load roadmap items')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      const selected = roadmapItemsList.find((item) => item.id === formData.roadmap_item_id)
      if (!selected) {
        showError('Please select a roadmap item')
        setSubmitting(false)
        return
      }

      if (!selected.target_date && !formData.due_at) {
        showError('Selected roadmap item does not have a target date. Please select an item with a target date.')
        setSubmitting(false)
        return
      }

      await reminderEngine.createReminderForRoadmapItem(formData.roadmap_item_id, {
        reminderDaysBefore: parseInt(formData.reminder_days_before),
        notificationChannels: formData.notification_channels,
        title: formData.title || selected.title,
        description: formData.description,
        dueAt: formData.due_at ? new Date(formData.due_at).toISOString() : null,
      })

      showSuccess('Reminder created successfully')
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Error creating reminder:', error)
      showError(`Failed to create reminder: ${error.message}`)
      setSubmitting(false)
    }
  }

  const handleChannelToggle = (channel) => {
    setFormData((prev) => {
      const channels = prev.notification_channels || []
      if (channels.includes(channel)) {
        return {
          ...prev,
          notification_channels: channels.filter((c) => c !== channel),
        }
      } else {
        return {
          ...prev,
          notification_channels: [...channels, channel],
        }
      }
    })
  }

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-reminder-title"
        className="dialog-content"
        style={{ width: 480, maxHeight: '90vh', overflow: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dialog-header">
          <h2 id="new-reminder-title" className="dialog-title">New reminder</h2>
        </div>
        <CardContent>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Roadmap Item Select */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: 'var(--fg)' }}>
                Roadmap Item *
              </label>
              {loading ? (
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--fg-muted)' }}>Loading...</p>
              ) : (
                <Select value={formData.roadmap_item_id} onValueChange={(value) => setFormData({ ...formData, roadmap_item_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder='Select a roadmap item...' />
                  </SelectTrigger>
                  <SelectContent>
                    {roadmapItemsList.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.title} {item.target_date ? `(${item.target_date})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Title */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: 'var(--fg)' }}>
                Title (optional)
              </label>
              <Input
                placeholder='Leave blank to use roadmap item title'
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            {/* Description */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: 'var(--fg)' }}>
                Description (optional)
              </label>
              <textarea
                placeholder='Additional details about this reminder'
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
                rows={3}
              />
            </div>

            {/* Days Before */}
            <label className="v2-field"><span>Specific reminder date & time (optional)</span><input type="datetime-local" value={formData.due_at} onChange={e => setFormData({ ...formData, due_at: e.target.value })}/><small>Choose an exact time, or use days before the initiative’s target date at 09:00 in your timezone.</small></label>
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: 'var(--fg)' }}>
                Days Before Target Date
              </label>
              <Input
                type='number'
                disabled={!!formData.due_at}
                min='0'
                max='90'
                value={formData.reminder_days_before}
                onChange={(e) => setFormData({ ...formData, reminder_days_before: e.target.value })}
              />
            </div>

            {/* Notification Channels */}
            <div>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: 'var(--fg)' }}>
                Notification Channels
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                {(integrationsEnabled ? ['in_app', 'push', 'slack'] : ['in_app']).map((channel) => (
                  <label
                    key={channel}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      background: 'var(--bg-inset)',
                      border: formData.notification_channels?.includes(channel) ? '1px solid var(--accent)' : '1px solid var(--border)',
                    }}
                  >
                    <input
                      type='checkbox'
                      checked={formData.notification_channels?.includes(channel)}
                      onChange={() => handleChannelToggle(channel)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '13px', color: 'var(--fg)', textTransform: 'capitalize' }}>{channel}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
              <Button variant='outline' onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type='submit' variant='primary' disabled={submitting}>
                {submitting ? 'Creating...' : 'Create Reminder'}
              </Button>
            </div>
          </form>
        </CardContent>
      </div>
    </div>
  )
}
